import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { gameApi, siteApi } from "@/lib/api";
import { normalizeBackendRichText } from "@/utils/backendRichText";
import { UsdtDepositPanel } from "./CryptoDepositSections";
import WalletHistorySection from "./WalletHistorySection";
import WalletNoticeModal from "./WalletNoticeModal";
import {
  formatAmountInput,
  formatKrwToUsdt,
  formatMoney,
  formatPoint,
  formatUsdt,
  getBonusId,
  getBonusTitle,
  getCryptoWalletAddress,
  getHeadquartersUsdtAddress,
  getHistoryAmount,
  getMoneyTypeTabs,
  getProfileAccountNumber,
  getProfileBalance,
  getProfileDepositorName,
  getProfileUsdtAddress,
  getResponseRows,
  getResponseTotal,
  getResultMessage,
  getSelectedBank,
  getUsdtRate,
  getWalletNoticeHtml,
  HISTORY_LIMIT,
  isAnonymousProfile,
  isSuccessResult,
  isUsdtWalletInfoEnabled,
  MONEY_TYPES,
  maskAccountNumber,
  QUICK_AMOUNTS,
  toNumber,
  WALLET_VIEWS,
} from "./financeWallet.helpers";

const VIEW_CONFIG = {
  [WALLET_VIEWS.DEPOSIT]: {
    title: "입금",
    description: "입금계좌 문의 후 금액을 신청해주세요.",
    balanceType: "money",
    balanceLabel: "보유금",
    unit: "원",
    submitLabel: "입금 신청",
    historyType: "CREDIT",
  },
  [WALLET_VIEWS.WITHDRAW]: {
    title: "출금",
    description: "출금 가능 금액과 비밀번호를 확인해주세요.",
    balanceType: "money",
    balanceLabel: "출금 가능",
    unit: "원",
    submitLabel: "출금 신청",
    historyType: "DEBIT",
  },
  [WALLET_VIEWS.POINT]: {
    title: "포인트",
    description: "보유 포인트를 게임머니로 전환합니다.",
    balanceType: "point",
    balanceLabel: "보유 포인트",
    unit: "P",
    submitLabel: "포인트 전환",
  },
  [WALLET_VIEWS.COMP]: {
    title: "콤프",
    description: "보유 콤프를 게임머니로 전환합니다.",
    balanceType: "comp",
    balanceLabel: "보유 콤프",
    unit: "P",
    submitLabel: "콤프 전환",
  },
};

const normalizeAmountInput = (value) => String(value || "").replace(/[^\d]/g, "");
const HISTORY_MIN_ROWS = 4;

const WalletPanel = ({ view, profile, settings, onBack, onRefreshWallet }) => {
  const config = VIEW_CONFIG[view] || VIEW_CONFIG[WALLET_VIEWS.DEPOSIT];
  const isFinanceView = view === WALLET_VIEWS.DEPOSIT || view === WALLET_VIEWS.WITHDRAW;
  const [moneyType, setMoneyType] = useState(MONEY_TYPES.GENERAL);
  const [amount, setAmount] = useState("");
  const [password, setPassword] = useState("");
  const [accountPassword, setAccountPassword] = useState("");
  const [selectedBonusId, setSelectedBonusId] = useState("");
  const [bonusOptions, setBonusOptions] = useState([]);
  const [bankRows, setBankRows] = useState([]);
  const [historyRows, setHistoryRows] = useState([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyPage, setHistoryPage] = useState(1);
  const [cryptoWallet, setCryptoWallet] = useState(null);
  const [cryptoRate, setCryptoRate] = useState(null);
  const [cryptoWalletError, setCryptoWalletError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [bankLoading, setBankLoading] = useState(false);
  const [bonusLoading, setBonusLoading] = useState(false);
  const [cryptoLoading, setCryptoLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [accountInquirySubmitting, setAccountInquirySubmitting] = useState(false);
  const [isGuideOpen, setIsGuideOpen] = useState(false);
  const [notice, setNotice] = useState(null);
  const contentRequestIdRef = useRef(0);
  const walletRetryRequestIdRef = useRef(0);
  const isMountedRef = useRef(true);
  const cryptoWalletInfoLoadedRef = useRef(false);

  const isAnonymous = isAnonymousProfile(profile);
  const userUsdtAddress = getProfileUsdtAddress(profile);
  const headquartersUsdtAddress = getHeadquartersUsdtAddress(settings);
  const profileScopeKey = String(
    profile?.id || profile?.user_id || profile?.member_id || profile?.user_unique_id || "guest"
  );
  const activeUsdtRate = getUsdtRate(settings, cryptoRate);
  const selectedBank = useMemo(() => getSelectedBank(bankRows, profile), [bankRows, profile]);
  const moneyTypeTabs = useMemo(
    () =>
      isFinanceView
        ? getMoneyTypeTabs(settings, { includeUsdt: view === WALLET_VIEWS.DEPOSIT })
        : [],
    [isFinanceView, settings, view]
  );
  const activeMoneyType = moneyTypeTabs.some((tab) => tab.key === moneyType)
    ? moneyType
    : moneyTypeTabs[0]?.key || MONEY_TYPES.GENERAL;
  const isUsdtMode = isFinanceView && activeMoneyType === MONEY_TYPES.USDT;
  const isUsdtDepositMode = view === WALLET_VIEWS.DEPOSIT && isUsdtMode;
  const isHoldemMode = isFinanceView && activeMoneyType === MONEY_TYPES.HOLDEM;

  const activeBalanceType = useMemo(() => {
    if (!isFinanceView) return config.balanceType;
    return isHoldemMode ? "holdemMoney" : "money";
  }, [config.balanceType, isFinanceView, isHoldemMode]);

  const activeBalanceLabel = useMemo(() => {
    if (!isFinanceView) return config.balanceLabel;
    if (isHoldemMode) return view === WALLET_VIEWS.WITHDRAW ? "홀덤 출금 가능" : "홀덤머니";
    if (isUsdtMode) return view === WALLET_VIEWS.WITHDRAW ? "USDT 출금 기준" : "USDT 입금 기준";
    return config.balanceLabel;
  }, [config.balanceLabel, isFinanceView, isHoldemMode, isUsdtMode, view]);

  const activeDescription = useMemo(() => {
    if (isUsdtDepositMode) return "TRC-20 입금 주소를 확인하고 전송해주세요.";
    if (isUsdtMode && view === WALLET_VIEWS.WITHDRAW) {
      return "등록된 USDT 주소와 환산 금액을 확인해주세요.";
    }
    if (isHoldemMode) return view === WALLET_VIEWS.WITHDRAW ? "홀덤머니 출금을 신청합니다." : "홀덤머니 입금을 신청합니다.";
    return config.description;
  }, [config.description, isHoldemMode, isUsdtDepositMode, isUsdtMode, view]);

  const balance = useMemo(() => getProfileBalance(profile, activeBalanceType), [profile, activeBalanceType]);
  const balanceText = config.unit === "원" ? `${formatMoney(balance)} 원` : formatPoint(balance);
  const guideHtml = useMemo(
    () => normalizeBackendRichText(getWalletNoticeHtml(settings, profile, view)),
    [profile, settings, view]
  );
  const historyCount = historyTotal || historyRows.length;
  const totalPages = Math.max(1, Math.ceil(historyCount / HISTORY_LIMIT));
  const historySkeletonRows = Math.max(HISTORY_MIN_ROWS, Math.min(HISTORY_LIMIT, historyRows.length || 0));

  const showNotice = useCallback((type, message) => {
    setNotice({ type, message });
  }, []);

  const applyCryptoWalletResult = useCallback((walletResult) => {
    const wallet = isSuccessResult(walletResult) ? walletResult.data : null;
    const walletAddress = getCryptoWalletAddress(wallet);
    if (walletAddress) {
      setCryptoWallet(wallet);
      setCryptoWalletError("");
      return;
    }

    setCryptoWallet(null);
    setCryptoWalletError(
      getResultMessage(
        walletResult,
        "개인 USDT 입금 주소를 불러오지 못했습니다. 잠시 후 다시 시도해주세요."
      )
    );
  }, []);

  const retryCryptoWallet = useCallback(async () => {
    const requestId = walletRetryRequestIdRef.current + 1;
    walletRetryRequestIdRef.current = requestId;
    setCryptoLoading(true);
    setCryptoWallet(null);
    setCryptoWalletError("");

    try {
      const walletResult = await gameApi.finance.getCryptoWallet().catch((error) => ({
        success: false,
        error: error?.message,
      }));
      if (!isMountedRef.current || walletRetryRequestIdRef.current !== requestId) return;
      cryptoWalletInfoLoadedRef.current = true;
      applyCryptoWalletResult(walletResult);
    } finally {
      if (isMountedRef.current && walletRetryRequestIdRef.current === requestId) {
        setCryptoLoading(false);
      }
    }
  }, [applyCryptoWalletResult]);

  const loadContent = useCallback(
    async ({ silent = false, pageOverride } = {}) => {
      const requestId = contentRequestIdRef.current + 1;
      contentRequestIdRef.current = requestId;
      const isCurrentRequest = () => contentRequestIdRef.current === requestId;
      let ownsCryptoLoading = false;
      if (!silent) setIsLoading(true);
      const targetPage = pageOverride || historyPage;

      try {
        if (isFinanceView) {
          if (isUsdtDepositMode) {
            const shouldLoadWalletInfo = !cryptoWalletInfoLoadedRef.current;
            setBonusOptions([]);
            if (shouldLoadWalletInfo) {
              ownsCryptoLoading = true;
              setCryptoLoading(true);
              setCryptoWallet(null);
              setCryptoWalletError("");
            }
            const [walletResult, rateResult, depositResult] = await Promise.all([
              shouldLoadWalletInfo
                ? gameApi.finance.getCryptoWallet().catch((error) => ({
                    success: false,
                    error: error?.message,
                  }))
                : Promise.resolve(null),
              shouldLoadWalletInfo
                ? gameApi.finance.getCryptoRate().catch(() => null)
                : Promise.resolve(null),
              gameApi.finance
                .getCryptoDeposits({ page: targetPage, limit: HISTORY_LIMIT })
                .catch(() => null),
            ]);

            if (!isCurrentRequest()) return;

            if (shouldLoadWalletInfo) {
              cryptoWalletInfoLoadedRef.current = true;
              applyCryptoWalletResult(walletResult);
              setCryptoRate(rateResult && isSuccessResult(rateResult) ? rateResult.data : null);
            }
            if (depositResult && isSuccessResult(depositResult)) {
              const rows = getResponseRows(depositResult.data);
              setHistoryRows(rows);
              setHistoryTotal(getResponseTotal(depositResult.data, rows));
            } else {
              setHistoryRows([]);
              setHistoryTotal(0);
            }
            return;
          }

          const bonusParams = { money_type: activeMoneyType };
          if (profile?.level !== undefined && profile?.level !== null) {
            bonusParams.level = profile.level;
          }
          const shouldLoadBonus = view === WALLET_VIEWS.DEPOSIT;
          if (shouldLoadBonus) setBonusLoading(true);
          const [listResult, bonusResult] = await Promise.all([
            gameApi.finance.getDepositWithdrawalList({
              type: config.historyType,
              page: targetPage,
              limit: HISTORY_LIMIT,
              money_type: activeMoneyType,
            }),
            shouldLoadBonus
              ? gameApi.finance.getBonusList(bonusParams)
              : Promise.resolve(null),
          ]);
          if (!isCurrentRequest()) return;

          if (isSuccessResult(listResult)) {
            const rows = getResponseRows(listResult.data);
            setHistoryRows(rows);
            setHistoryTotal(getResponseTotal(listResult.data, rows));
          } else {
            setHistoryRows([]);
            setHistoryTotal(0);
          }

          if (shouldLoadBonus) {
            setBonusOptions(isSuccessResult(bonusResult) ? getResponseRows(bonusResult.data) : []);
          } else {
            setBonusOptions([]);
          }
          return;
        }

        const result =
          view === WALLET_VIEWS.POINT
            ? await gameApi.finance.getPointHistory({ page: targetPage, limit: HISTORY_LIMIT })
            : await gameApi.finance.getCompHistory({
                page: targetPage,
                limit: HISTORY_LIMIT,
                money_type: MONEY_TYPES.GENERAL,
              });
        if (!isCurrentRequest()) return;

        if (isSuccessResult(result)) {
          const rows = getResponseRows(result.data);
          setHistoryRows(rows);
          setHistoryTotal(getResponseTotal(result.data, rows));
        } else {
          setHistoryRows([]);
          setHistoryTotal(0);
        }
        setBonusOptions([]);
      } finally {
        if (isCurrentRequest()) {
          if (!silent) setIsLoading(false);
          setBonusLoading(false);
          if (ownsCryptoLoading) setCryptoLoading(false);
        }
      }
    },
    [
      applyCryptoWalletResult,
      activeMoneyType,
      config.historyType,
      historyPage,
      isFinanceView,
      isUsdtDepositMode,
      profile?.level,
      view,
    ]
  );

  useEffect(() => {
    setMoneyType(MONEY_TYPES.GENERAL);
    setAmount("");
    setPassword("");
    setAccountPassword("");
    setSelectedBonusId("");
    setHistoryPage(1);
    cryptoWalletInfoLoadedRef.current = false;
    setCryptoWallet(null);
    setCryptoWalletError("");
    setIsGuideOpen(false);
    setNotice(null);
    walletRetryRequestIdRef.current += 1;
  }, [view]);

  useEffect(() => {
    setHistoryPage(1);
    cryptoWalletInfoLoadedRef.current = false;
    setCryptoWallet(null);
    setCryptoRate(null);
    setCryptoWalletError("");
    walletRetryRequestIdRef.current += 1;
  }, [profileScopeKey]);

  useEffect(() => {
    if (!isFinanceView || !moneyTypeTabs.length) return;
    if (!moneyTypeTabs.some((tab) => tab.key === moneyType)) {
      setMoneyType(moneyTypeTabs[0].key);
    }
  }, [isFinanceView, moneyType, moneyTypeTabs]);

  useEffect(() => {
    setAmount("");
    setPassword("");
    setAccountPassword("");
    setSelectedBonusId("");
    setHistoryPage(1);
    cryptoWalletInfoLoadedRef.current = false;
    setCryptoWallet(null);
    setCryptoWalletError("");
    setNotice(null);
    walletRetryRequestIdRef.current += 1;
  }, [moneyType]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      contentRequestIdRef.current += 1;
      walletRetryRequestIdRef.current += 1;
    };
  }, []);

  useEffect(() => {
    if (!isFinanceView) return undefined;
    let mounted = true;
    setBankLoading(true);
    siteApi
      .getBanks({ limit: "ALL" })
      .then((result) => {
        if (!mounted) return;
        setBankRows(isSuccessResult(result) ? getResponseRows(result.data) : []);
      })
      .finally(() => {
        if (mounted) setBankLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [isFinanceView]);

  useEffect(() => {
    loadContent();
    return () => {
      contentRequestIdRef.current += 1;
    };
  }, [loadContent, profileScopeKey]);

  const updateAmount = (value) => {
    setAmount(normalizeAmountInput(value));
  };

  const addQuickAmount = (value) => {
    setAmount(String(toNumber(amount) + toNumber(value)));
  };

  const resetAmount = () => {
    setAmount("");
  };

  const copyToClipboard = async (value) => {
    if (!value) {
      showNotice("error", "복사할 주소가 없습니다.");
      return;
    }

    try {
      await navigator.clipboard.writeText(value);
      showNotice("success", "주소가 복사되었습니다.");
    } catch {
      showNotice("error", "주소 복사에 실패했습니다.");
    }
  };

  const handleAccountInquiry = async () => {
    if (accountInquirySubmitting) return;
    if (settings?.enter_exchange_password_when_account_inquiry && !accountPassword.trim()) {
      showNotice("error", "계좌문의 비밀번호를 입력해주세요.");
      return;
    }

    setAccountInquirySubmitting(true);
    try {
      const payload = settings?.enter_exchange_password_when_account_inquiry
        ? { password: accountPassword }
        : {};
      const result = await gameApi.finance.requestAccountInquiry(payload);
      showNotice(
        isSuccessResult(result) ? "success" : "error",
        getResultMessage(
          result,
          isSuccessResult(result) ? "입금계좌 문의가 접수되었습니다." : "입금계좌 문의에 실패했습니다."
        )
      );
      if (isSuccessResult(result)) setAccountPassword("");
    } finally {
      setAccountInquirySubmitting(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (isSubmitting) return;

    if (isUsdtDepositMode) {
      showNotice("success", "표시된 TRC-20 주소로 입금하면 확인 후 자동 반영됩니다.");
      return;
    }

    const numericAmount = toNumber(amount);
    if (!numericAmount || numericAmount <= 0) {
      showNotice("error", "금액을 입력해주세요.");
      return;
    }

    if (view === WALLET_VIEWS.WITHDRAW && !password.trim()) {
      showNotice("error", "출금 비밀번호를 입력해주세요.");
      return;
    }

    if (view === WALLET_VIEWS.DEPOSIT) {
      if (bonusLoading) {
        showNotice("error", "보너스 목록을 불러오는 중입니다.");
        return;
      }
      if (bonusOptions.length > 0 && !selectedBonusId) {
        showNotice("error", "입금 보너스를 선택해주세요.");
        return;
      }
    }

    setIsSubmitting(true);
    try {
      let result;
      if (view === WALLET_VIEWS.DEPOSIT) {
        const payload = { amount: numericAmount };
        if (activeMoneyType !== MONEY_TYPES.GENERAL) payload.money_type = activeMoneyType;
        if (selectedBonusId) payload.bonus_id = selectedBonusId;
        result = await gameApi.finance.requestDeposit(payload);
      } else if (view === WALLET_VIEWS.WITHDRAW) {
        const payload = {
          amount: numericAmount,
          password,
        };
        if (activeMoneyType === MONEY_TYPES.HOLDEM) {
          payload.money_type = MONEY_TYPES.HOLDEM;
        }
        result = await gameApi.finance.requestWithdrawal(payload);
      } else if (view === WALLET_VIEWS.POINT) {
        result = await gameApi.finance.convertPoint({ amount: numericAmount });
      } else {
        result = await gameApi.finance.convertComp({ amount: numericAmount });
      }

      const succeeded = isSuccessResult(result);
      showNotice(
        succeeded ? "success" : "error",
        getResultMessage(result, succeeded ? "신청이 완료되었습니다." : "신청 처리에 실패했습니다.")
      );

      if (succeeded) {
        setAmount("");
        setPassword("");
        setSelectedBonusId("");
        setHistoryPage(1);
        await loadContent({ silent: true, pageOverride: 1 });
        if (onRefreshWallet) await onRefreshWallet();
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderMoneyTabs = () =>
    moneyTypeTabs.length > 1 ? (
      <div className="wallet-money-tabs" role="tablist" aria-label="입출금 종류">
        {moneyTypeTabs.map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={activeMoneyType === tab.key ? "is-active" : ""}
            onClick={() => setMoneyType(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>
    ) : null;

  const renderInfoTiles = () => {
    if (!isFinanceView) return null;

    const accountNumber = getProfileAccountNumber(profile);
    const depositorName = getProfileDepositorName(profile);
    const tiles = [];

    if (isAnonymous) {
      tiles.push({
        label: "내 USDT 주소",
        value: userUsdtAddress || "등록되지 않음",
      });
    } else {
      tiles.push(
        {
          label: "은행",
          value: bankLoading ? "확인 중" : selectedBank?.bank_name || "-",
        },
        {
          label: "예금주",
          value: depositorName || "-",
        },
        {
          label: "계좌번호",
          value: maskAccountNumber(accountNumber),
        }
      );
      if (isUsdtMode) {
        tiles.push({
          label: "내 USDT 주소",
          value: userUsdtAddress || "등록되지 않음",
        });
      }
    }

    tiles.push({
      label: isHoldemMode ? "홀덤머니" : "보유머니",
      value: `${formatMoney(balance)} 원`,
    });

    if (isUsdtMode && activeUsdtRate > 0) {
      tiles.push({
        label: "USDT 환율",
        value: `1 USDT = ${formatMoney(activeUsdtRate)} 원`,
      });
    }

    return (
      <div className="wallet-info-grid">
        {tiles.map((tile) => (
          <div className="wallet-info-tile" key={`${tile.label}-${tile.value}`}>
            <span>{tile.label}</span>
            <strong>{tile.value}</strong>
          </div>
        ))}
      </div>
    );
  };

  const renderAmountField = (label = "금액") => (
    <div className="wallet-field">
      <label htmlFor={`wallet-amount-${view}-${activeMoneyType}`}>{label}</label>
      <div className="wallet-input-shell">
        <input
          id={`wallet-amount-${view}-${activeMoneyType}`}
          type="text"
          inputMode="numeric"
          value={formatAmountInput(amount)}
          onChange={(event) => updateAmount(event.target.value)}
          placeholder="금액 입력"
        />
        <span>{config.unit}</span>
      </div>
      <div className="wallet-quick-amounts">
        {QUICK_AMOUNTS.map((quickAmount) => (
          <button type="button" key={quickAmount} onClick={() => addQuickAmount(quickAmount)}>
            {formatMoney(quickAmount)}
          </button>
        ))}
        <button type="button" className="is-reset" onClick={resetAmount}>
          정정
        </button>
      </div>
    </div>
  );

  const renderAnonymousUsdtInfo = () => {
    if (!isAnonymous || !isUsdtWalletInfoEnabled(settings)) return null;
    return (
      <div className="wallet-usdt-lite">
        <div>
          <span>USDT 입금 주소</span>
          <strong>{headquartersUsdtAddress || "등록된 주소가 없습니다."}</strong>
        </div>
        <button type="button" onClick={() => copyToClipboard(headquartersUsdtAddress)} disabled={!headquartersUsdtAddress}>
          복사
        </button>
      </div>
    );
  };

  const renderDepositFields = () => (
    <>
      <div className="wallet-inline-actions">
        <button
          type="button"
          className="wallet-secondary-button"
          onClick={handleAccountInquiry}
          disabled={accountInquirySubmitting}
        >
          {accountInquirySubmitting ? "문의 중" : "입금계좌 문의"}
        </button>
        <p>계좌 정보가 필요한 경우 먼저 문의를 접수해주세요.</p>
      </div>

      {settings?.enter_exchange_password_when_account_inquiry ? (
        <div className="wallet-field">
          <label htmlFor="wallet-account-password">계좌문의 비밀번호</label>
          <div className="wallet-input-shell">
            <input
              id="wallet-account-password"
              type="password"
              value={accountPassword}
              onChange={(event) => setAccountPassword(event.target.value)}
              placeholder="출금 비밀번호"
            />
          </div>
        </div>
      ) : null}

      {bonusOptions.length > 0 ? (
        <div className="wallet-field">
          <label htmlFor="wallet-bonus">입금 보너스</label>
          <select
            id="wallet-bonus"
            value={selectedBonusId}
            onChange={(event) => setSelectedBonusId(event.target.value)}
            disabled={bonusLoading}
          >
            <option value="">{bonusLoading ? "보너스 확인 중" : "보너스 선택"}</option>
            {bonusOptions.map((bonus, index) => {
              const bonusId = getBonusId(bonus);
              return (
                <option key={bonusId || `${getBonusTitle(bonus)}-${index}`} value={bonusId || ""}>
                  {getBonusTitle(bonus)}
                </option>
              );
            })}
          </select>
        </div>
      ) : null}

      {renderAnonymousUsdtInfo()}
      {isAnonymous && activeUsdtRate > 0 ? (
        <div className="wallet-usdt-rate">
          <span>USDT 환산</span>
          <strong>{formatKrwToUsdt(amount, activeUsdtRate)} USDT</strong>
        </div>
      ) : null}
    </>
  );

  const renderWithdrawFields = () => (
    <>
      <div className="wallet-field">
        <label htmlFor="wallet-withdraw-password">출금 비밀번호</label>
        <div className="wallet-input-shell">
          <input
            id="wallet-withdraw-password"
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            placeholder="출금 비밀번호"
          />
        </div>
      </div>

      {isUsdtWalletInfoEnabled(settings) ? (
        <div className="wallet-usdt-rate">
          <span>{isUsdtMode ? "USDT 출금 환산" : "USDT 환산"}</span>
          <strong>{formatKrwToUsdt(amount, activeUsdtRate)} USDT</strong>
        </div>
      ) : null}
    </>
  );

  const historyUnit = isUsdtDepositMode ? "USDT" : config.unit === "원" ? "원" : "P";
  const historyTitle =
    view === WALLET_VIEWS.DEPOSIT
      ? isUsdtMode
        ? "USDT 입금 내역"
        : "입금 신청 내역"
      : view === WALLET_VIEWS.WITHDRAW
        ? "출금 신청 내역"
        : "최근 내역";

  const renderHistoryAmount = (row) => {
    const historyAmount = getHistoryAmount(row);
    if (historyUnit === "USDT") return `${formatUsdt(historyAmount)} USDT`;
    return `${formatMoney(historyAmount)} ${historyUnit}`;
  };

  const renderHistoryLabel = (row) => {
    if (isUsdtMode || row?.money_type === MONEY_TYPES.USDT) return "USDT";
    if (row?.money_type === MONEY_TYPES.HOLDEM) return "홀덤";
    return config.title;
  };

  return (
    <div className="cashier-detail">
      <button type="button" className="wallet-back-button" onClick={onBack}>
        <span aria-hidden="true">‹</span>
        {config.title}
      </button>

      {renderMoneyTabs()}

      <section className="wallet-summary-card">
        <div>
          <span>{activeBalanceLabel}</span>
          <strong>{balanceText}</strong>
        </div>
        <p>{activeDescription}</p>
      </section>

      {renderInfoTiles()}

      {guideHtml ? (
        <section className={`wallet-guide ${isGuideOpen ? "is-open" : ""}`}>
          <button type="button" className="wallet-guide-toggle" onClick={() => setIsGuideOpen((open) => !open)}>
            <span>안내사항</span>
            <strong>{isGuideOpen ? "접기" : "안내보기"}</strong>
          </button>
          {isGuideOpen ? (
            <div className="wallet-guide-content" dangerouslySetInnerHTML={{ __html: guideHtml }} />
          ) : null}
        </section>
      ) : null}

      <form className="wallet-form" onSubmit={handleSubmit}>
        {!isUsdtDepositMode ? renderAmountField(view === WALLET_VIEWS.WITHDRAW ? "출금 금액" : "금액") : null}
        {view === WALLET_VIEWS.DEPOSIT ? (
          isUsdtDepositMode ? (
            <UsdtDepositPanel
              activeUsdtRate={activeUsdtRate}
              cryptoLoading={cryptoLoading}
              cryptoWallet={cryptoWallet}
              cryptoWalletError={cryptoWalletError}
              onCopyAddress={copyToClipboard}
              onRetryWallet={retryCryptoWallet}
            />
          ) : (
            renderDepositFields()
          )
        ) : null}
        {view === WALLET_VIEWS.WITHDRAW ? renderWithdrawFields() : null}

        {!isUsdtDepositMode ? (
          <button type="submit" className="wallet-submit-button" disabled={isSubmitting}>
            {isSubmitting ? "처리 중" : config.submitLabel}
          </button>
        ) : null}
      </form>

      <WalletHistorySection
        currentPage={historyPage}
        historyCount={historyCount}
        historyRows={historyRows}
        historySkeletonRows={historySkeletonRows}
        historyTitle={historyTitle}
        isCryptoHistory={isUsdtDepositMode}
        isLoading={isLoading}
        onNextPage={() => setHistoryPage((page) => Math.min(totalPages, page + 1))}
        onPreviousPage={() => setHistoryPage((page) => Math.max(1, page - 1))}
        renderAmount={renderHistoryAmount}
        renderLabel={renderHistoryLabel}
        totalPages={totalPages}
      />

      <WalletNoticeModal notice={notice} onClose={() => setNotice(null)} />
    </div>
  );
};

export default WalletPanel;
